import React from 'react'
import { Box } from '@mui/material'
import { ThemeProvider, createTheme, useTheme } from '@mui/material/styles'
import { deepmerge } from '@mui/utils'
import type { Company } from '../../../core/models/Company'
import { buildCompanyBrandingThemeParts } from '../../../utils/companyBrandingPalette'

export interface CompanyBrandedShellProps {
  company: Company | null | undefined
  children: React.ReactNode
}

/**
 * Scoped company branding: nested MUI theme + `--sevv-*` overrides for this subtree only
 * (does not touch document root). No-op when primary_color is missing or invalid.
 */
export const CompanyBrandedShell: React.FC<CompanyBrandedShellProps> = ({ company, children }) => {
  const outerTheme = useTheme()
  const parts = React.useMemo(
    () => buildCompanyBrandingThemeParts(company?.primary_color, company?.secondary_color, company?.settings ?? null),
    [
      company?.primary_color,
      company?.secondary_color,
      company?.settings?.accent_color,
      company?.settings?.link_color,
      company?.settings?.surface_tint_color,
    ],
  )

  const innerTheme = React.useMemo(() => {
    if (!parts) return null
    const paletteMerge: Record<string, unknown> = {
      primary: parts.muiPalette.primary,
      info: parts.muiPalette.info,
    }
    if (parts.muiPalette.secondary) {
      paletteMerge.secondary = parts.muiPalette.secondary
    }
    return createTheme(
      deepmerge(outerTheme, {
        palette: paletteMerge,
        components: {
          MuiLink: {
            styleOverrides: {
              root: {
                color: parts.linkColorHex,
              },
            },
          },
        },
      }),
    )
  }, [outerTheme, parts])

  if (!parts || !innerTheme) {
    return <>{children}</>
  }

  return (
    <ThemeProvider theme={innerTheme}>
      <Box
        sx={{
          color: 'var(--sevv-text-primary)',
          ...parts.cssVariables,
        }}
      >
        {children}
      </Box>
    </ThemeProvider>
  )
}
